import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Users, PackageOpen, ScrollText } from "lucide-react";
import { api } from "@/lib/api";

const RulesCard = () => (
  <Link
    to="/rules"
    data-testid="home-cb-rules-button"
    style={{ animationDelay: "240ms" }}
    className="rise group relative mt-5 sm:mt-6 flex flex-col sm:flex-row sm:items-center gap-5 overflow-hidden rounded-3xl border border-amber-400/20 bg-zinc-900/70 p-7 sm:p-8 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-amber-400/50"
  >
    <div className="absolute -left-20 -bottom-24 h-64 w-64 rounded-full blur-3xl opacity-20 bg-amber-500 transition-opacity duration-500 group-hover:opacity-40" />
    <span className="relative h-12 w-12 shrink-0 rounded-2xl grid place-items-center bg-amber-500/15 text-amber-300"><ScrollText className="h-6 w-6" /></span>
    <div className="relative flex-1">
      <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 font-semibold">Before you join</div>
      <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mt-1">CB Rules</h2>
      <p data-testid="home-cb-rules-description" className="text-zinc-400 mt-2 text-sm sm:text-base">Please be sure to have reviewed the CB Rules so that we can have a smooth CB!</p>
    </div>
    <span className="relative inline-flex items-center gap-1.5 self-start sm:self-center rounded-full bg-amber-400 px-4 py-2 text-sm font-semibold text-zinc-950 transition-colors duration-200 group-hover:bg-amber-300">
      Read the rules <ArrowUpRight className="h-4 w-4" />
    </span>
  </Link>
);

const EntryCard = ({ to, testId, icon: Icon, kicker, title, body, stat, statLabel, tone, delay }) => (
  <Link
    to={to}
    data-testid={testId}
    style={{ animationDelay: delay }}
    className={`rise group relative overflow-hidden rounded-3xl border border-white/10 bg-zinc-900/70 p-7 sm:p-10 min-h-[320px] flex flex-col transition-[transform,border-color] duration-300 hover:-translate-y-1 ${tone.border}`}
  >
    <div className={`absolute -right-24 -top-24 h-72 w-72 rounded-full blur-3xl opacity-30 transition-opacity duration-500 group-hover:opacity-60 ${tone.glow}`} />
    <div className="relative flex items-start justify-between">
      <span className={`h-12 w-12 rounded-2xl grid place-items-center ${tone.icon}`}>
        <Icon className="h-6 w-6" />
      </span>
      <ArrowUpRight className="h-7 w-7 text-zinc-500 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-white" />
    </div>
    <div className="relative mt-auto pt-12">
      <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 font-semibold">{kicker}</div>
      <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-2">{title}</h2>
      <p className="text-zinc-400 mt-3 max-w-md text-sm sm:text-base">{body}</p>
      <div className="mt-6 flex items-baseline gap-2">
        <span className="font-mono text-3xl font-bold text-white">{stat ?? "–"}</span>
        <span className="text-sm text-zinc-500">{statLabel}</span>
      </div>
    </div>
  </Link>
);

export default function Home() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <section className="rise max-w-3xl">
        <div className="inline-flex items-center gap-2 rounded-full border border-pink-500/30 bg-pink-500/10 px-3 py-1 text-xs font-semibold tracking-wider text-pink-300 uppercase">
          <span className="h-1.5 w-1.5 rounded-full bg-pink-400 animate-pulse" /> Group order hub
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-5 leading-[1.05]">
          Golden Hour 6<br />
          <span className="bg-gradient-to-r from-violet-400 via-pink-400 to-amber-300 bg-clip-text text-transparent">with ArekuTrades and ThePastelFix</span>
        </h1>
        <p className="text-zinc-400 mt-5 text-base md:text-lg max-w-xl">
          Pick your name to see your claims and payments, or jump into the open drops and grab a slot before the sets fill.
        </p>
      </section>

      <section className="grid md:grid-cols-2 gap-5 sm:gap-6 mt-12">
        <EntryCard
          to="/joiners" testId="home-joiners-button" icon={Users} kicker="For joiners" title="Joiners"
          body="Find your name to view your claims, submit payment proof and check your balance."
          stat={stats?.joiners} statLabel="joiners" delay="80ms"
          tone={{ border: "hover:border-violet-500/50", glow: "bg-violet-600", icon: "bg-violet-500/15 text-violet-300" }}
        />
        <EntryCard
          to="/drops" testId="home-open-drops-button" icon={PackageOpen} kicker="Live now" title="Open Drops"
          body="Access current open drops and submit your claims"
          stat={stats?.open_drops} statLabel={`open drops · ${stats?.open_slots ?? 0} slots left`} delay="160ms"
          tone={{ border: "hover:border-pink-500/50", glow: "bg-pink-600", icon: "bg-pink-500/15 text-pink-300" }}
        />
      </section>
      <RulesCard />
    </div>
  );
}
